import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";

export function useStars() {
  const { user, openAuth } = useAuth();
  const [ids, setIds] = useState(new Set());
  const [events, setEvents] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [viralIds, setViralIds] = useState(new Set());
  const [loaded, setLoaded] = useState(false);

  const load = useCallback(async () => {
    if (!user) { setIds(new Set()); setEvents([]); setAlerts([]); setViralIds(new Set()); setLoaded(true); return; }
    try {
      const { data } = await api.get("/catalysts/starred");
      setIds(new Set(data.ids)); setEvents(data.events); setAlerts(data.alerts || []); setViralIds(new Set(data.viralIds || []));
    } catch { /* ignore */ } finally { setLoaded(true); }
  }, [user]);
  useEffect(() => { load(); }, [load]);

  const toggle = useCallback(async (event) => {
    if (!user) { toast("Log in to star catalysts", { description: "Build your personal coming-up feed." }); openAuth(); return; }
    const starred = ids.has(event.id);
    setIds((s) => { const n = new Set(s); starred ? n.delete(event.id) : n.add(event.id); return n; });
    try {
      if (starred) await api.delete(`/catalysts/${event.id}/star`);
      else await api.post(`/catalysts/${event.id}/star`);
      toast.success(starred ? `Unstarred ${event.title}` : `Starred ${event.title}`);
      load();
    } catch (err) {
      if (err.response?.status === 402) toast.error("Starring catalysts is a Pro feature", { action: { label: "Go Pro", onClick: () => (window.location.href = "/pricing") } });
      else toast.error("Couldn't update star");
      load();
    }
  }, [user, ids, openAuth, load]);

  const markAlertsSeen = useCallback(async () => {
    if (!alerts.length) return;
    setAlerts([]);
    try { await api.post("/catalysts/alerts/seen"); } catch { /* ignore */ }
  }, [alerts.length]);

  return { starredIds: ids, starredEvents: events, alerts, viralIds, markAlertsSeen, loaded, toggle, isStarred: (id) => ids.has(id), reload: load };
}
